import { useQuery } from '@tanstack/react-query';
import type { AssetSummary } from '@prism/contracts';
import { api } from '../client';
import { queryKeys } from '../queryKeys';
import { usePaged } from './paged';
import type { SequenceSummary } from './production';

export interface CollectionDTO {
  id: string; parentId: string | null; name: string; description: string; assets: number; sequences: number; updatedAt: string;
  /** personal: yours; public: everyone's; shared: someone's personal folder shared with you. */
  scope: 'personal' | 'public' | 'shared'; owner: string | null; canEdit: boolean; canManage: boolean;
  shares: Array<{ userId: string; name: string; canEdit: boolean }>;
}
interface CollectionItemsDTO {
  collection: { id: string; name: string; description: string; parentId: string | null; scope: CollectionDTO['scope']; canEdit: boolean };
  assets: AssetSummary[];
  sequences: SequenceSummary[];
  nextCursor?: string | null;
}
export function useCollections() {
  return useQuery({ queryKey: queryKeys.collections(), queryFn: () => api<{ items: CollectionDTO[]; canCreatePublic: boolean }>('/api/v1/collections') });
}
export function useCollectionItems(id: string | null) {
  // 200 items a page (1.0.0-rc.3); Load more fetches the next.
  return usePaged<never, CollectionItemsDTO & { items: never[] }>({
    queryKey: queryKeys.collectionItems(id), url: `/api/v1/collections/${id}/items`, enabled: Boolean(id), pageSize: 200,
    join: (pages) => ({ ...pages[0]!, items: [], assets: pages.flatMap((p) => p.assets), sequences: pages.flatMap((p) => p.sequences), nextCursor: pages.at(-1)!.nextCursor ?? null }),
  });
}
export function useCollectionsContaining(subject: 'asset' | 'sequence', id: string, enabled = true) {
  return useQuery({
    queryKey: queryKeys.collectionsContaining(subject, id),
    queryFn: () => api<{ ids: string[] }>(`/api/v1/collections/containing?${subject}Id=${id}`),
    enabled,
  });
}
